import { useState, useEffect, useCallback, useMemo } from 'react';
import { marked } from 'marked';
import AxiosInstance from '@/api/axios';
import { toast } from '@/components/Base/Toast/ToastManager';
import Button from '@/components/Base/Button';
import { Dialog } from '@/components/Base/Headless';
import Lucide from '@/components/Base/Lucide';
import DOMPurify from 'dompurify';

interface ReleaseNote {
  id: string;
  title: string;
  body: string;
  publishedAt: string;
  url?: string;
  hasOlder?: boolean;
  hasNewer?: boolean;
  hasFeedback?: boolean;
}

interface ReleaseNotesModalProps {
  externalOpen?: boolean;
  onExternalClose?: () => void;
}

type FeedbackSentiment = 'sad' | 'neutral' | 'happy';

const FEEDBACK_OPTIONS: {
  sentiment: FeedbackSentiment;
  emoji: string;
  label: string;
}[] = [
  { sentiment: 'sad', emoji: '😞', label: 'Not great' },
  { sentiment: 'neutral', emoji: '😐', label: 'Okay' },
  { sentiment: 'happy', emoji: '😃', label: 'Love it' },
];

const ReleaseNotesModal = ({
  externalOpen,
  onExternalClose,
}: ReleaseNotesModalProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const [note, setNote] = useState<ReleaseNote | null>(null);
  const [loading, setLoading] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    if (externalOpen) {
      setShowDialog(true);
      onExternalClose?.();
    }
  }, [externalOpen]);

  const fetchLatestNote = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AxiosInstance.get(`/release-notes/latest`);
      setNote(res.data.data);
    } catch (err: any) {
      toast.show({
        title: 'Error',
        content:
          err?.response?.data?.message ?? 'Failed to fetch release notes',
        type: 'warning',
        duration: 2000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const navigate = useCallback(
    async (direction: 'older' | 'newer') => {
      if (!note?.publishedAt) return;
      setLoading(true);
      try {
        const param = direction === 'older' ? 'before' : 'after';
        const res = await AxiosInstance.get(`/release-notes/adjacent`, {
          params: { [param]: note.publishedAt },
        });
        const adjacent: ReleaseNote | null = res.data.data;
        console.log('Adjacent release note:', adjacent);
        setNote(adjacent);
      } catch (err: any) {
        toast.show({
          title: 'Error',
          content:
            err?.response?.data?.message ?? 'Failed to fetch release note',
          type: 'warning',
          duration: 2000,
        });
      } finally {
        setLoading(false);
      }
    },
    [note?.publishedAt]
  );

  useEffect(() => {
    if (showDialog) fetchLatestNote();
  }, [showDialog, fetchLatestNote]);

  const handleFeedback = useCallback(
    async (sentiment: FeedbackSentiment) => {
      if (!note || submittingFeedback) return;

      setSubmittingFeedback(true);
      try {
        const res = await AxiosInstance.post(
          `/release-notes/${note.id}/feedback`,
          { sentiment }
        );
        setNote(res.data.data);
      } catch (err: any) {
        toast.show({
          title: 'Error',
          content: err?.response?.data?.message ?? 'Failed to submit feedback',
          type: 'warning',
          duration: 2000,
        });
      } finally {
        setSubmittingFeedback(false);
      }
    },
    [note, submittingFeedback]
  );

  const dateLabel = note
    ? new Date(note.publishedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  // Parse markdown ->HTML only when the note body changes
  const renderedHtml = useMemo(() => {
    if (!note?.body) return '';
    const rawHtml = marked.parse(note.body, { breaks: true }) as string;
    return DOMPurify.sanitize(rawHtml);
  }, [note?.body]);

  return (
    <Dialog size='md' open={showDialog} onClose={() => setShowDialog(false)}>
      <Dialog.Panel className='p-0 overflow-hidden !w-[40rem] h-full flex flex-col'>
        <div className='relative pb-6 flex-shrink-0'>
          <div className='flex items-center justify-between relative'>
            <Dialog.Title
              as='h2'
              className='text-xl border-b-black/10 border-2 font-semibold text-slate-800 dark:text-slate-100 w-full'
            >
              <div className='px-1 py-1 font-normal'>Latest Updates</div>
            </Dialog.Title>
            <Button
              variant='plain'
              type='button'
              className='border-none shadow-none p-1 absolute right-4 '
              onClick={() => setShowDialog(false)}
            >
              <Lucide
                icon='X'
                className='w-6 h-6 text-black dark:text-slate-400'
              />
            </Button>
          </div>
        </div>

        <Dialog.Description
          as='div'
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          className='flex flex-col px-10 pt-5 pb-6 h-full max-h-[84%] overflow-y-auto text-left'
        >
          {loading && (
            <p className='text-center text-sm text-slate-400 py-10'>
              Loading...
            </p>
          )}

          {!loading && note && (
            <>
              <div className='flex items-center gap-2 flex-wrap mb-1.5'>
                <span className=' text-xs text-slate-500 dark:text-slate-400'>
                  {dateLabel} by Krisha Tiwari
                </span>
              </div>

              <div
                className='
                  text-base font-normal leading-relaxed text-[#6b7280] dark:text-slate-300
                  [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-[#161e2e] dark:[&_h1]:text-slate-100 [&_h1]:mt-4 [&_h1]:mb-4
                  [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-[#161e2e] dark:[&_h2]:text-slate-100 [&_h2]:my-4
                  [&_h3]:text-sm [&_h3]:font-bold [&_h3]:text-[#161e2e] [&_h3]:mt-4 [&_h3]:mb-1.5
                  [&_p]:my-2
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2
                  [&_li]:mb-2
                  [&_strong]:font-semibold [&_strong]:underline [&_strong]:text-[#3182ce] [&_strong]:font-bold
                  [&_u]:text-[#3182ce] [&_u]:font-bold
                  [&_a]:text-primary [&_a]:underline
                '
                dangerouslySetInnerHTML={{ __html: renderedHtml }}
              />

              <div className='mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 flex flex-col gap-4'>
                {note.hasFeedback ? (
                  <p className='text-sm text-slate-500 dark:text-slate-400'>
                    🎉 Thanks for your feedback!
                  </p>
                ) : (
                  <div className='flex items-center flex-wrap gap-3'>
                    <span className='text-sm italic text-slate-500 dark:text-slate-400'>
                      Tell us what you think:
                    </span>
                    <div className='flex items-center gap-2'>
                      {FEEDBACK_OPTIONS.map(({ sentiment, emoji, label }) => (
                        <button
                          key={sentiment}
                          type='button'
                          title={label}
                          aria-label={label}
                          onClick={() => handleFeedback(sentiment)}
                          disabled={submittingFeedback}
                          className='text-xl leading-none rounded-full p-1 transition-transform hover:scale-110 disabled:opacity-50'
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {(note.hasNewer || note.hasOlder) && (
                  <div className='flex justify-center gap-3'>
                    {note.hasNewer && (
                      <button
                        className='text-sm text-[#3182ce] px-3 py-1.5 rounded-md hover:bg-[#f1f1f1]'
                        onClick={() => navigate('newer')}
                      >
                        Show previous release
                      </button>
                    )}

                    {note.hasOlder && (
                      <button
                        className='text-sm text-[#3182ce] px-3 py-1.5 rounded-md hover:bg-[#f1f1f1]'
                        onClick={() => navigate('older')}
                      >
                        Show next release
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {!loading && !note && (
            <p className='text-center text-sm text-slate-400 py-10'>
              No release notes available.
            </p>
          )}
        </Dialog.Description>
      </Dialog.Panel>
    </Dialog>
  );
};

export default ReleaseNotesModal;
